// MessengerInviteUtils.js
import { therapistClient } from '../../../../PatientApps/apiClient';
import { v4 as uuidv4 } from 'uuid';

/**
 * Creates an invite link for a specific therapist
 * @param {string} therapistUuid - The UUID of the therapist creating the invite
 * @param {string} therapistName - The name of the therapist creating the invite (only used for UI)
 * @returns {Promise<{chat_uuid: string, inviteUrl: string}>} The invite code and shareable URL
 */
export async function createChatInvite(therapistUuid, therapistName) {
  try {
    // Generate a UUID for the invite code
    const chat_uuid = uuidv4();
    
    // Get base URL from window.location or environment
    const baseUrl = window.location.origin;
    const inviteUrl = `${baseUrl}/chat/${chat_uuid}`;
    
    // Create an entry in the database to track this invite
    const response = await therapistClient.post('/api/protected/therapist/chats/chat-invites/create', {
      chat_uuid: chat_uuid,
    });
    
    console.log('Chat invite created:', response.data);
    
    return { 
      
      inviteUrl
    };
  } catch (error) {
    console.error('Error creating chat invite:', error);
    throw error;
  }
}

/**
 * Copies the invite URL to clipboard
 * @param {string} url - The URL to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyInviteUrl(url) {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Error copying invite URL:', error);
    return false;
  }
}

/**
 * Validates if an invite code is valid and active
 * @param {string} chat_uuid - The invite code to validate
 * @returns {Promise<{valid: boolean, therapistUuid?: string, therapistName?: string}>} Validation result
 */
export async function validateInviteCode(chat_uuid) {
  try {
    const response = await therapistClient.get(`/api/protected/therapist/chats/chat-invites/${chat_uuid}`);
    
    if (response.data && response.data.status === 'active') {
      // Check if invite has expired
      const expiresAt = new Date(response.data.expires_at);
      if (expiresAt < new Date()) {
        return { valid: false, reason: 'expired' };
      }
      
      return { 
        valid: true, 
        therapistUuid: response.data.therapist_uuid,
        therapistName: response.data.therapist_name
      };
    }
    
    return { valid: false };
  } catch (error) {
    console.error('Error validating invite code:', error);
    return { valid: false, reason: 'error' };
  }
}

/**
 * Shares the invite link via the web Share API if available
 * @param {string} url - The URL to share
 * @param {string} therapistName - The therapist's name
 * @returns {Promise<boolean>} Success status
 */
export async function shareInviteLink(url, therapistName) {
  if (navigator.share) {
    try {
      await navigator.share({
        title: `Chat Invitation from ${therapistName}`,
        text: `You've been invited to chat with ${therapistName}. Click the link to join.`,
        url: url
      });
      return true;
    } catch (error) {
      console.error('Error sharing:', error);
      return false;
    }
  } else {
    // Fallback to copy to clipboard if Web Share API is not available
    return copyInviteUrl(url);
  }
}